import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "./api";
import type { HqSuggestion } from "./hq-rule";
import type { CountRule } from "./count-rule";

// Mirrors apps/api/src/lib/webhook-mapping.ts — keep in sync.
export type FilterOp =
  | "equals"
  | "equals_or_missing"
  | "not_equals"
  | "contains"
  | "exists"
  | "not_exists";

export interface MappingFilterRule {
  path: string;
  op: FilterOp;
  value?: string;
}

export interface WebhookMapping {
  /** Special integration (code-based projection) instead of path mapping. */
  preset?: "digistore24" | "ablefy";
  keys?: Record<string, string>;
  fields?: Record<string, string>;
  custom_data?: Record<string, string>;
  /** "Only track if": all rules must match or the event is dropped. */
  filter?: { rules: MappingFilterRule[] };
  /** HQ-Lead-Regel (TASK-233); der Editor dafür ist `HqRuleEditor` (TASK-234), hier nur der Typ. */
  hq_rule?: unknown;
}

export interface WebhookEndpoint {
  id: number;
  project_id: number;
  name: string;
  slug: string;
  event_type_default: string;
  status: "detecting" | "active" | "disabled";
  mapping_json: WebhookMapping | null;
  /** null = inherit project default; true/false = explicit override. */
  signing_required: boolean | null;
  created_at: string;
  updated_at: string;
  /** Who last activated this endpoint (TASK-147) — null = unknown/never. */
  activated_by: "partner" | "admin" | null;
  /** Zählregel (TASK-187/189) — wirkt sofort auf neu eingehende Events:
   *  eine Wiederholung wird als "<Name>-Repeat" gespeichert und erreicht
   *  nie CAPI. Ein rückwirkendes Angleichen läuft separat (TASK-190). */
  count_rule: CountRule;
  count_interval_days: number | null;
}

export interface WebhookSample {
  id: number;
  payload: unknown;
  received_at: string;
}

/** A retroactive count-rule resync run (TASK-190, S7/S8). */
export interface CountRuleResyncRun {
  id: number;
  event_type: string;
  rule: CountRule;
  interval_days: number | null;
  status: "running" | "done" | "failed";
  renamed_count: number;
  deferred_count: number;
  started_at: string;
  finished_at: string | null;
  error_class: string | null;
}

const LIST_KEY = (projectId: number) =>
  ["admin", "webhook-endpoints", projectId] as const;
const DETAIL_KEY = (projectId: number, endpointId: number) =>
  ["admin", "webhook-endpoints", projectId, endpointId] as const;

export function useWebhookEndpoints(projectId: number | null) {
  return useQuery<{ items: WebhookEndpoint[] }>({
    queryKey: LIST_KEY(projectId ?? 0),
    queryFn: () => api(`/admin/projects/${projectId}/webhook-endpoints`),
    enabled: projectId !== null && projectId > 0,
    staleTime: 30_000,
  });
}

export function useCreateWebhookEndpoint(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      eventType: string;
      slug?: string;
      countRule?: CountRule;
      countIntervalDays?: number | null;
    }) =>
      api<{ ok: true; id: number; slug: string }>(
        `/admin/projects/${projectId}/webhook-endpoints`,
        { method: "POST", body: JSON.stringify(input) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY(projectId) }),
  });
}

export function useWebhookEndpoint(
  projectId: number | null,
  endpointId: number | null,
) {
  return useQuery<{
    endpoint: WebhookEndpoint;
    samples: WebhookSample[];
    lastRun: CountRuleResyncRun | null;
  }>({
    queryKey: DETAIL_KEY(projectId ?? 0, endpointId ?? 0),
    queryFn: () =>
      api(`/admin/projects/${projectId}/webhook-endpoints/${endpointId}`),
    enabled:
      projectId !== null &&
      endpointId !== null &&
      projectId > 0 &&
      endpointId > 0,
    // Sample list updates live while detecting; also how the S8-4 last-run
    // status keeps polling while a retroactive resync is still running.
    refetchInterval: 5_000,
  });
}

export function useUpdateWebhookEndpoint(
  projectId: number,
  endpointId: number,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name?: string;
      eventType?: string;
      status?: WebhookEndpoint["status"];
      mappingJson?: WebhookMapping;
      signingRequired?: boolean | null;
      countRule?: CountRule;
      countIntervalDays?: number | null;
      /** S8-3: only meaningful alongside countRule — books a resync run. */
      retroactive?: boolean;
    }) =>
      api<{ ok: true }>(
        `/admin/projects/${projectId}/webhook-endpoints/${endpointId}`,
        { method: "PATCH", body: JSON.stringify(input) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY(projectId) });
      qc.invalidateQueries({ queryKey: DETAIL_KEY(projectId, endpointId) });
    },
  });
}

export function useDeleteWebhookEndpoint(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (endpointId: number) =>
      api<{ ok: true }>(
        `/admin/projects/${projectId}/webhook-endpoints/${endpointId}`,
        { method: "DELETE" },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY(projectId) }),
  });
}

export function useAnalyzeWebhookEndpoint(
  projectId: number,
  endpointId: number,
) {
  return useMutation({
    mutationFn: (
      input: {
        sampleIds?: number[];
        customPrompt?: string;
        /** "Payload identisch wie bei …" — analyze another endpoint's samples. */
        sampleEndpointId?: number;
      } = {},
    ) => {
      const body: Record<string, unknown> = {};
      if (input.sampleIds && input.sampleIds.length > 0) {
        body.sampleIds = input.sampleIds;
      }
      if (input.customPrompt && input.customPrompt.trim()) {
        body.customPrompt = input.customPrompt;
      }
      if (input.sampleEndpointId) {
        body.sampleEndpointId = input.sampleEndpointId;
      }
      return api<{
        mapping: WebhookMapping;
        source: "llm" | "heuristic" | "fallback" | "preset";
        notes: string | null;
        samplesUsed: number;
        model: "fable";
      }>(
        `/admin/projects/${projectId}/webhook-endpoints/${endpointId}/analyze`,
        {
          method: "POST",
          body:
            Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
        },
      );
    },
  });
}

/** TASK-235: AI proposal of the HQ rule from one sentence; stores nothing. */
export function suggestHqRule(
  projectId: number,
  endpointId: number,
  sentence: string,
): Promise<HqSuggestion> {
  return api<HqSuggestion>(
    `/admin/projects/${projectId}/webhook-endpoints/${endpointId}/hq-rule/suggest`,
    { method: "POST", body: JSON.stringify({ sentence }) },
  );
}

export function useReplaySample(projectId: number, endpointId: number) {
  return useMutation({
    mutationFn: (sampleId: number) =>
      api<{ ok: true; event_id: string }>(
        `/admin/projects/${projectId}/webhook-endpoints/${endpointId}/replay-sample`,
        { method: "POST", body: JSON.stringify({ sampleId }) },
      ),
  });
}
